import { useConversationScope } from "@/hooks/useConversationScope";
import type { IAutocompleteItem, ICollection, Tools } from "@/types";
import { rag_v1 } from "@/utils/axios-v2";
import {
  useQuery,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useCallback, useId, useLayoutEffect, useRef, useState } from "react";
import { composerQueryKeys } from "../composer-query-keys";

/** A new visit to A is not the previous A editor/request session. */
export const useComposerSession = (conversationId?: string) => {
  const queryScope = useConversationScope();
  const instanceId = useId();
  const identity = JSON.stringify([
    queryScope.identity,
    conversationId ?? null,
    queryScope.ready,
  ]);
  const [previousOwner, setOwner] = useState({ identity, generation: 0 });
  let owner = previousOwner;
  if (previousOwner.identity !== identity) {
    owner = { identity, generation: previousOwner.generation + 1 };
    setOwner(owner);
  }
  const current = useRef<typeof owner | null>(null);
  useLayoutEffect(() => {
    current.current = owner;
    return () => {
      current.current = null;
    };
  }, [owner]);
  const isCurrent = useCallback(
    () => queryScope.ready && current.current === owner,
    [queryScope.ready, owner],
  );
  return {
    ...queryScope,
    conversationId,
    token: `${instanceId}:${owner.generation}`,
    isCurrent,
  };
};

type ComposerSession = ReturnType<typeof useComposerSession>;

export const useComposerToolsQuery = () => {
  const queryScope = useConversationScope();
  // Leaving readiness must detach the active observer too, so a logout/bootstrap
  // interval cancels an old request even when the canonical IDs do not change.
  const toolsKey = composerQueryKeys.tools(queryScope.scope);
  const queryKey = queryScope.ready
    ? toolsKey
    : ([...toolsKey, "unready"] as const);
  const requestScope = queryScope.requestScope;
  const query = useQuery<Tools>({
    queryKey,
    queryFn: ({ signal }) => {
      const config = { requestScope, signal };
      return rag_v1
        .get<Tools>("agent-tools/tools/", config)
        .then((response) => response.data);
    },
    enabled: queryScope.ready,
  });
  return {
    ...query,
    queryKey,
    data: queryScope.ready ? query.data : undefined,
  };
};

export const useComposerAutocompleteQuery = (
  session: ComposerSession,
  search: string,
  enabled: boolean,
) => {
  const queryKey = composerQueryKeys.autocomplete(
    session.scope,
    session.conversationId,
    session.token,
    search,
  );
  const requestScope = session.requestScope;
  const params = { search, conversation_id: session.conversationId };
  const query = useQuery<IAutocompleteItem[]>({
    queryKey,
    queryFn: ({ signal }) => {
      const config = { requestScope, signal, params };
      return rag_v1
        .get<IAutocompleteItem[]>("agent-tools/autocomplete/", config)
        .then((response) => response.data || []);
    },
    enabled: session.ready && enabled && Boolean(search),
  });
  return { ...query, queryKey, data: session.ready ? query.data : undefined };
};

export const useComposerCollectionsQuery = (
  session: ComposerSession,
  toolId: string | undefined,
  search: string,
  enabled: boolean,
) => {
  const queryKey = composerQueryKeys.collections(
    session.scope,
    session.conversationId,
    session.token,
    toolId,
    search,
  );
  const requestScope = session.requestScope;
  const params = { search, conversation_id: session.conversationId };
  const query = useQuery<ICollection[]>({
    queryKey,
    queryFn: ({ signal }) => {
      const config = { requestScope, signal, params };
      return rag_v1
        .get<ICollection[]>(`agent-tools/autocomplete/${toolId}`, config)
        .then((response) => response.data || []);
    },
    enabled: session.ready && enabled && Boolean(toolId),
  });
  return { ...query, queryKey, data: session.ready ? query.data : undefined };
};

/** Recheck the originating cache at selection and submit, not only the visible menu. */
export const isComposerItemCurrent = (
  client: QueryClient,
  queryKey: QueryKey,
  item: { id: string },
) => {
  const state = client.getQueryState<Array<{ id: string }>>(queryKey);
  return (
    state?.status === "success" &&
    !state.isInvalidated &&
    Boolean(state.data?.some((candidate) => candidate.id === item.id))
  );
};
